import {IsomorphicScript} from '@aboutyou/head-elements';
import React from 'react';
import type {CheckoutExperiment} from '../../hooks/useCheckoutEntryDetails';

export const BnplPaymentSorting: React.FC<{
  experiments: CheckoutExperiment[];
}> = ({experiments}) => {
  const bnplExperiment = experiments.find(
    experiment => experiment.experimentKey === 'ab_zbnpl',
  );

  if (!bnplExperiment || bnplExperiment.variantKey === 'azbnpl-c') {
    return null;
  }

  return (
    <IsomorphicScript
      id="Bnpl-Payment-Sorting"
      content={`(${sortPaymentListScript.toString()})("${
        bnplExperiment.variantKey
      }")`}
    />
  );
};

const sortPaymentListScript = (variantKey: string) => {
  const RETRY_DELAY_MS = 30;
  const KLARNA_PAY_LATER_KEY = 'spay_klarna_paylater';
  const ZBNPL_KEY = 'spay_zps_bnpl';

  const tryAttach = () => {
    if (!window.scayleCheckoutFunctions) {
      setTimeout(tryAttach, RETRY_DELAY_MS);
      return;
    }

    // Sort the BNPL payment option relative to KlarnaPayLater based on the AB test variant.
    // Variant 1 (azbnpl-1): KlarnaPayLater first, then ZBNPL immediately after.
    // Variant 2 (azbnpl-2): ZBNPL first, then KlarnaPayLater immediately after.
    // https://scayle.dev/documentation/storefront/checkout/look-and-feel/dynamic-slots/integrating-slots#sorting-payment-options
    window.scayleCheckoutFunctions.sortPaymentOptions = paymentOptions => {
      if (!paymentOptions) {
        return;
      }

      const options = [...paymentOptions.generic];
      const zbnplIndex = options.findIndex(
        o => o.paymentOptionKey === ZBNPL_KEY,
      );
      const klarnaPayLaterIndex = options.findIndex(
        o => o.paymentOptionKey === KLARNA_PAY_LATER_KEY,
      );

      if (zbnplIndex === -1 || klarnaPayLaterIndex === -1) {
        return paymentOptions;
      }

      const zbnpl = options[zbnplIndex];
      if (!zbnpl) {
        return paymentOptions;
      }

      options.splice(zbnplIndex, 1); // Remove ZBNPL from its current position
      const newKlarnaPayLaterIndex = options.findIndex(
        o => o.paymentOptionKey === KLARNA_PAY_LATER_KEY,
      );

      if (variantKey === 'azbnpl-1') {
        // Variant 1: KlarnaPayLater #1, Z-BNPL #2
        options.splice(newKlarnaPayLaterIndex + 1, 0, zbnpl);
      } else if (variantKey === 'azbnpl-2') {
        // Variant 2: Z-BNPL #1, KlarnaPayLater #2
        options.splice(newKlarnaPayLaterIndex, 0, zbnpl);
      }

      return {
        ...paymentOptions,
        generic: options,
      };
    };
  };

  tryAttach();
};
